import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, X, UserX, UserCheck, Send, MoreHorizontal, User } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const ASSIGNABLE_ROLES = [
  { key: "owner",            label: "Garage Owner" },
  { key: "branch_manager",   label: "Branch Manager" },
  { key: "advisor",          label: "Service Advisor" },
  { key: "mechanic",         label: "Mechanic" },
  { key: "storekeeper",      label: "Storekeeper" },
  { key: "purchase_executive", label: "Purchase Executive" },
  { key: "cashier",          label: "Cashier" },
  { key: "driver",           label: "Driver" },
  { key: "security",         label: "Security or Gate User" },
  { key: "accountant",       label: "Accountant or Auditor" },
];

export default function UsersSettings() {
  const { user } = useAuth();
  const canManage = user?.role === "owner";
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [showRole, setShowRole] = useState("");
  const [addOpen, setAddOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/users");
      setRows(r.data || []);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = rows.filter((u) => {
    if (showRole && u.role !== showRole) return false;
    if (!q.trim()) return true;
    const s = q.toLowerCase();
    return (
      (u.name || "").toLowerCase().includes(s) ||
      (u.email || "").toLowerCase().includes(s) ||
      (u.phone || "").toLowerCase().includes(s)
    );
  });

  const roleLabel = (k) => ASSIGNABLE_ROLES.find((r) => r.key === k)?.label || k;

  const suspend = async (uid) => {
    if (!window.confirm("Suspend this user? They will lose access immediately.")) return;
    try { await api.post(`/settings/users/${uid}/suspend`); toast.success("User suspended"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not suspend"); }
  };
  const reactivate = async (uid) => {
    try { await api.post(`/settings/users/${uid}/reactivate`); toast.success("User reactivated"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not reactivate"); }
  };
  const resend = async (uid) => {
    try { await api.post(`/settings/users/${uid}/resend-invitation`); toast.success("Invitation resent"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not resend"); }
  };

  return (
    <div className="space-y-4" data-testid="settings-users">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex-1 min-w-[220px] relative">
          <input value={q} onChange={(e) => setQ(e.target.value)}
                 placeholder="Search by name, email or phone"
                 data-testid="users-search"
                 className="w-full h-10 px-3 rounded-lg border border-[#E4E7EC] text-[13px] bg-white focus:border-[#457b9d] outline-none" />
        </div>
        <select value={showRole} onChange={(e) => setShowRole(e.target.value)}
                data-testid="users-role-filter"
                className="h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] bg-white">
          <option value="">All roles</option>
          {ASSIGNABLE_ROLES.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
        </select>
        {canManage && (
          <button onClick={() => setAddOpen(true)} data-testid="users-add-btn"
                  className="h-10 px-4 rounded-md bg-[#e63946] hover:bg-[#d02f3c] text-white text-[12.5px] font-semibold inline-flex items-center gap-1.5">
            <Plus size={13} /> Add User
          </button>
        )}
      </div>

      <section className="bg-white rounded-xl border border-[#E4E7EC]">
        {loading ? (
          <div className="p-10 text-center text-[13px] text-[#667085]">Loading users…</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center" data-testid="users-empty">
            <div className="w-12 h-12 mx-auto rounded-full bg-[#F1FAEE] grid place-items-center text-[#457b9d] mb-3">
              <User size={20} />
            </div>
            <div className="text-[14px] font-semibold text-[#1d3557]">No users match your filters</div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]" data-testid="users-table">
              <thead>
                <tr className="text-left text-[10.5px] uppercase tracking-wider font-semibold text-[#667085] border-b border-[#F1FAEE]">
                  <th className="px-5 py-2.5">Name</th>
                  <th className="px-3 py-2.5">Phone</th>
                  <th className="px-3 py-2.5">Email</th>
                  <th className="px-3 py-2.5">Role</th>
                  <th className="px-3 py-2.5">Status</th>
                  {canManage && <th className="px-5 py-2.5 text-right">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {filtered.map((u) => (
                  <tr key={u.id} data-testid={`user-row-${u.id}`}
                      className="border-b border-[#F1FAEE] last:border-0 hover:bg-[#F7F9FC]">
                    <td className="px-5 py-3 font-semibold text-[#1d3557]">{u.name || "—"}</td>
                    <td className="px-3 py-3 font-mono text-[11.5px] text-[#667085]">{u.phone || "—"}</td>
                    <td className="px-3 py-3 text-[#667085]">{u.email || "—"}</td>
                    <td className="px-3 py-3">
                      <span className="inline-flex px-2 py-0.5 rounded-full text-[10.5px] font-semibold uppercase tracking-wider bg-[#457b9d]/12 text-[#1d3557]">
                        {roleLabel(u.role)}
                      </span>
                    </td>
                    <td className="px-3 py-3">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-[10.5px] font-semibold uppercase tracking-wider ${
                        u.active ? "bg-[#12B76A]/12 text-[#0F9256]" : "bg-[#e63946]/12 text-[#e63946]"
                      }`}>
                        {u.active ? "Active" : "Suspended"}
                      </span>
                    </td>
                    {canManage && (
                      <td className="px-5 py-3 text-right">
                        <div className="inline-flex gap-1">
                          {u.active ? (
                            <button onClick={() => suspend(u.id)}
                                    data-testid={`user-suspend-${u.id}`}
                                    className="h-8 px-2 rounded-md border border-[#E4E7EC] text-[11px] font-semibold text-[#e63946] hover:bg-[#e63946]/8 inline-flex items-center gap-1">
                              <UserX size={11} /> Suspend
                            </button>
                          ) : (
                            <button onClick={() => reactivate(u.id)}
                                    data-testid={`user-reactivate-${u.id}`}
                                    className="h-8 px-2 rounded-md border border-[#E4E7EC] text-[11px] font-semibold text-[#0F9256] hover:bg-[#12B76A]/10 inline-flex items-center gap-1">
                              <UserCheck size={11} /> Reactivate
                            </button>
                          )}
                          <button onClick={() => resend(u.id)}
                                  data-testid={`user-resend-${u.id}`}
                                  className="h-8 px-2 rounded-md border border-[#E4E7EC] text-[11px] font-semibold text-[#1d3557] hover:bg-[#F1FAEE] inline-flex items-center gap-1">
                            <Send size={11} /> Resend
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {addOpen && (
        <AddUserDialog onClose={() => setAddOpen(false)}
                       onSaved={() => { setAddOpen(false); load(); }} />
      )}
    </div>
  );
}

function AddUserDialog({ onClose, onSaved }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", role: "mechanic", password: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const save = async () => {
    if (!form.name.trim() || !form.email.trim() || !form.password) {
      setError("Name, email and password are required"); return;
    }
    setSaving(true); setError(null);
    try {
      await api.post("/users", form);
      toast.success("User added"); onSaved();
    } catch (e) { setError(e.response?.data?.detail || "Could not add user"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
         onClick={onClose} data-testid="add-user-backdrop">
      <div onClick={(e) => e.stopPropagation()}
           className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden"
           data-testid="add-user-dialog">
        <header className="px-6 py-4 border-b border-[#E4E7EC] flex items-center justify-between bg-[#F7F9FC]">
          <h2 className="text-[16px] font-bold text-[#1d3557]">Add user</h2>
          <button onClick={onClose} className="w-8 h-8 rounded-md hover:bg-white grid place-items-center text-[#667085]">
            <X size={17} />
          </button>
        </header>
        <div className="p-5 space-y-3">
          <F label="Full name *">
            <I value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
               data-testid="add-user-name" />
          </F>
          <div className="grid grid-cols-2 gap-3">
            <F label="Email *">
              <I type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                 data-testid="add-user-email" />
            </F>
            <F label="Mobile">
              <I value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                 className="font-mono" data-testid="add-user-phone" />
            </F>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <F label="Role">
              <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
                      data-testid="add-user-role"
                      className="h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] w-full bg-white">
                {ASSIGNABLE_ROLES.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
              </select>
            </F>
            <F label="Initial password *">
              <I type="password" value={form.password}
                 onChange={(e) => setForm({ ...form, password: e.target.value })}
                 data-testid="add-user-password" />
            </F>
          </div>
          {error && (
            <div className="rounded-md border border-[#e63946]/30 bg-[#e63946]/10 px-3 py-2 text-[12px] text-[#e63946]"
                 data-testid="add-user-error">{error}</div>
          )}
        </div>
        <footer className="px-6 py-3.5 border-t border-[#E4E7EC] bg-[#F7F9FC] flex justify-end gap-2">
          <button onClick={onClose}
                  className="h-10 px-4 rounded-md border border-[#E4E7EC] text-[12.5px] font-semibold text-[#667085] hover:bg-white">
            Cancel
          </button>
          <button onClick={save} disabled={saving}
                  data-testid="add-user-submit"
                  className="h-10 px-5 rounded-md bg-[#e63946] hover:bg-[#d02f3c] text-white text-[12.5px] font-semibold disabled:opacity-50">
            {saving ? "Saving…" : "Add user"}
          </button>
        </footer>
      </div>
    </div>
  );
}

function F({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10.5px] uppercase tracking-wider font-semibold text-[#667085]">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
function I({ className = "", ...props }) {
  return (
    <input {...props}
           className={`h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] w-full bg-white focus:border-[#457b9d] outline-none ${className}`} />
  );
}
